import { motion, useMotionValue, useSpring, useTransform } from "framer-motion"
import { LogoMark } from "./Logo"
import { scrollToId } from "./Nav"

const hexPoints = (r, cx = 250, cy = 250) =>
  Array.from({ length: 6 }, (_, i) => {
    const a = ((60 * i - 90) * Math.PI) / 180
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)]
  })

const toPath = (pts) => pts.map((p) => p.join(",")).join(" ")

const rings = [
  { r: 235, dash: "2 10", speed: 90, dir: 1 },
  { r: 190, dash: "0", speed: 120, dir: -1 },
  { r: 145, dash: "6 6", speed: 70, dir: 1 },
]

const chips = [
  { label: "Sites", x: "4%", y: "18%", depth: 30 },
  { label: "E-commerce", x: "70%", y: "10%", depth: -24 },
  { label: "Sistemas", x: "76%", y: "72%", depth: 36 },
  { label: "SEO", x: "2%", y: "74%", depth: -30 },
]

const Chip = ({ chip, mx, my }) => {
  const x = useTransform(mx, (v) => v * chip.depth)
  const y = useTransform(my, (v) => v * chip.depth)
  return (
    <motion.span className="k-hero__chip" style={{ left: chip.x, top: chip.y, x, y }}>
      <i /> {chip.label}
    </motion.span>
  )
}

const Hero = () => {
  const mx = useSpring(useMotionValue(0), { stiffness: 60, damping: 20 })
  const my = useSpring(useMotionValue(0), { stiffness: 60, damping: 20 })
  const artX = useTransform(mx, (v) => v * -12)
  const artY = useTransform(my, (v) => v * -12)

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    mx.set((e.clientX - rect.left) / rect.width - 0.5)
    my.set((e.clientY - rect.top) / rect.height - 0.5)
  }

  const words = ["Convertendo", "sonhos", "e", "ideias", "em"]

  return (
    <section className="k-hero" id="top" onMouseMove={onMove}>
      <div className="k-hero__grid" aria-hidden />

      <div className="k-container k-hero__inner">
        <div className="k-hero__copy">
          <motion.div
            className="k-hero__status"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="k-dot" /> Agenda aberta para novos projetos
          </motion.div>

          <h1 className="k-hero__title">
            {words.map((w, i) => (
              <span className="k-hero__word" key={w + i}>
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.1 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                >
                  {w}
                </motion.span>
              </span>
            ))}
            <span className="k-hero__word">
              <motion.em
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                realidade.
              </motion.em>
            </span>
          </h1>

          <motion.p
            className="k-hero__lead"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            Sites, lojas virtuais e sistemas sob medida. Desenvolvimento moderno e escalável para o seu negócio
            crescer no digital — do primeiro rascunho ao suporte depois do lançamento.
          </motion.p>

          <motion.div
            className="k-hero__actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
          >
            <a
              href="#contato"
              className="k-btn k-btn--accent"
              onClick={(e) => {
                e.preventDefault()
                scrollToId("contato")
              }}
            >
              Impulsione seu negócio <span className="k-btn__arrow" aria-hidden>→</span>
            </a>
            <a
              href="#projetos"
              className="k-btn k-btn--ghost"
              onClick={(e) => {
                e.preventDefault()
                scrollToId("projetos")
              }}
            >
              Ver projetos
            </a>
          </motion.div>

          <motion.dl
            className="k-hero__facts"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
          >
            <div>
              <dt>10+</dt>
              <dd>projetos entregues</dd>
            </div>
            <div>
              <dt>7–15</dt>
              <dd>dias para um site no ar</dd>
            </div>
            <div>
              <dt>5,0★</dt>
              <dd>avaliação dos clientes</dd>
            </div>
          </motion.dl>
        </div>

        <motion.div
          className="k-hero__art"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden
        >
          <motion.div className="k-hero__orbit" style={{ x: artX, y: artY }}>
            {rings.map((ring) => (
              <motion.svg
                key={ring.r}
                viewBox="0 0 500 500"
                className="k-hero__svg"
                animate={{ rotate: 360 * ring.dir }}
                transition={{ duration: ring.speed, repeat: Infinity, ease: "linear" }}
              >
                <polygon
                  points={toPath(hexPoints(ring.r))}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeDasharray={ring.dash}
                  opacity={0.35}
                />
                {hexPoints(ring.r).map(([x, y], i) =>
                  i % 2 === 0 ? <circle key={i} cx={x} cy={y} r="4" className="k-hero__node" /> : null
                )}
              </motion.svg>
            ))}
            <svg viewBox="0 0 500 500" className="k-hero__svg">
              <polygon points={toPath(hexPoints(118))} className="k-hero__core" />
            </svg>
            <motion.div
              className="k-hero__mark"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <LogoMark />
            </motion.div>
          </motion.div>

          {chips.map((c) => (
            <Chip key={c.label} chip={c} mx={mx} my={my} />
          ))}

          <div className="k-hero__code">
            <span className="c-muted">const</span> projeto = <span className="c-accent">await</span> kjr
            <br />
            &nbsp;&nbsp;.transformar(<span className="c-string">"sua ideia"</span>)
          </div>
        </motion.div>
      </div>

      <div className="k-hero__scroll" aria-hidden>
        <span>role</span>
        <i />
      </div>
    </section>
  )
}

export default Hero
